import React from "react";
import { TreatmentPlanStep } from "../../../../services/treatmentPlanService";
import {
  StepCard,
  StepContent,
  StepTitle,
  StepDetails,
  StepDetailItem,
  StepDetailLabel,
  StepDetailValue,
  StepDetailHint,
} from "./TreatmentStep.styled";

interface TreatmentStepProps {
  step: TreatmentPlanStep;
}

/**
 * Treatment step (procedure) card component
 * Used within appointments to display individual procedures
 */
const TreatmentStep: React.FC<TreatmentStepProps> = ({ step }) => {
  // Get fee - use step.fee if available, otherwise calculate from code suggestions
  const fee =
    step.fee != null
      ? step.fee
      : step.codeSuggestions.reduce(
          (sum, code) => sum + (code.defaultFee || 0),
          0,
        );

  // Get duration - use step.durationMinutes if available, otherwise default 20
  const duration = step.durationMinutes ?? 20;

  // Get CDT codes as comma-separated string
  const cdtCodes = step.codeSuggestions.map((code) => code.code).join(", ");

  // Get explanation from CDT code descriptions
  const explanation = step.codeSuggestions
    .map((code) => code.description)
    .filter(Boolean)
    .join("\n");

  return (
    <StepCard>
      <StepContent>
        <StepTitle>{step.name}</StepTitle>
        <StepDetails>
          <StepDetailItem sx={{ flex: 1 }}>
            <StepDetailValue sx={{ fontWeight: 400, fontSize: "14px" }}>
              {explanation || "-"}
            </StepDetailValue>
          </StepDetailItem>
          <StepDetailItem>
            <StepDetailLabel>CDT Code:</StepDetailLabel>
            <StepDetailValue>{cdtCodes || "-"}</StepDetailValue>
          </StepDetailItem>
          <StepDetailItem>
            <StepDetailLabel>Duration:</StepDetailLabel>
            <StepDetailValue>{duration} min</StepDetailValue>
          </StepDetailItem>
          <StepDetailItem>
            <StepDetailLabel>Fee:</StepDetailLabel>
            <StepDetailValue>
              ${fee.toLocaleString("en-US")}{" "}
              <StepDetailHint>(based on clinic rates)</StepDetailHint>
            </StepDetailValue>
          </StepDetailItem>
        </StepDetails>
      </StepContent>
    </StepCard>
  );
};

export default TreatmentStep;
